export interface IAuthDebugEntry {
    ts: string,
    event: string,
    data?: Record<string, unknown>,
}

export const AUTH_DEBUG_STORAGE_KEY = 'vs_auth_debug_log';
export const AUTH_DEBUG_QUERY_PARAM_KEY = 'authDebug';
export const AUTH_DEBUG_ENV_VAR_NAME = 'REACT_APP_ENABLE_AUTH_DEBUG_LOGS';
export const AUTH_RUNTIME_CONFIG_LOG_QUERY_PARAM_KEY = 'authRuntimeConfigLog';
export const AUTH_RUNTIME_CONFIG_LOG_ENV_VAR_NAME = 'REACT_APP_ENABLE_AUTH_RUNTIME_CONFIG_LOGS';
export const AUTH_USER_LOG_QUERY_PARAM_KEY = 'authUserLog';
export const AUTH_USER_LOG_ENV_VAR_NAME = 'REACT_APP_ENABLE_AUTH_USER_LOGS';
const MAX_ENTRIES = 200;

interface IAuthDebugGateOptions {
    search?: string,
    envValue?: string,
    isDevelopment?: boolean,
}

interface IScopedAuthDebugGateOptions extends IAuthDebugGateOptions {
    queryParamKey: string,
}

function canUseWindow(): boolean {
    return typeof window !== 'undefined';
}

function readEntries(): IAuthDebugEntry[] {
    if (!canUseWindow()) return [];
    try {
        const raw = localStorage.getItem(AUTH_DEBUG_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed as IAuthDebugEntry[];
        return [];
    } catch {
        return [];
    }
}

function writeEntries(entries: IAuthDebugEntry[]): void {
    if (!canUseWindow()) return;
    try {
        localStorage.setItem(AUTH_DEBUG_STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
    } catch {
        // ignore
    }
}

export function isAuthDebugEnabled(): boolean {
    if (!canUseWindow()) return false;
    return shouldEnableAuthDebug({
        search: window.location.search,
        envValue: process.env.REACT_APP_ENABLE_AUTH_DEBUG_LOGS,
        isDevelopment: process.env.NODE_ENV === 'development',
    });
}

function shouldEnableScopedAuthDebug({
    search = '',
    envValue,
    isDevelopment = false,
    queryParamKey,
}: IScopedAuthDebugGateOptions): boolean {
    // Auth debug output stays unavailable outside development so production
    // builds cannot expose auth traces through URL or environment toggles.
    if (!isDevelopment) return false;
    // Env-based enablement keeps long-running local debugging sessions from
    // depending on a query param in every tab or refresh.
    if (envValue === 'true') return true;
    try {
        const searchParams = new URLSearchParams(search);
        return searchParams.get(queryParamKey) === '1';
    } catch {
        return false;
    }
}

export function shouldEnableAuthDebug({
    search = '',
    envValue,
    isDevelopment = false,
}: IAuthDebugGateOptions): boolean {
    return shouldEnableScopedAuthDebug({
        search,
        envValue,
        isDevelopment,
        queryParamKey: AUTH_DEBUG_QUERY_PARAM_KEY,
    });
}

export function shouldEnableAuthRuntimeConfigLog({
    search = '',
    envValue,
    isDevelopment = false,
}: IAuthDebugGateOptions): boolean {
    return shouldEnableScopedAuthDebug({
        search,
        envValue,
        isDevelopment,
        queryParamKey: AUTH_RUNTIME_CONFIG_LOG_QUERY_PARAM_KEY,
    });
}

export function shouldEnableAuthUserLog({
    search = '',
    envValue,
    isDevelopment = false,
}: IAuthDebugGateOptions): boolean {
    return shouldEnableScopedAuthDebug({
        search,
        envValue,
        isDevelopment,
        queryParamKey: AUTH_USER_LOG_QUERY_PARAM_KEY,
    });
}

export function isAuthUserLogEnabled(): boolean {
    if (!canUseWindow()) return false;
    return shouldEnableAuthUserLog({
        search: window.location.search,
        envValue: process.env.REACT_APP_ENABLE_AUTH_USER_LOGS,
        isDevelopment: process.env.NODE_ENV === 'development',
    });
}

function appendAuthEntry(prefix: string, entry: IAuthDebugEntry): void {
    // eslint-disable-next-line no-console
    console.info(prefix, entry);
    const entries = readEntries();
    entries.push(entry);
    writeEntries(entries);
}

export function logAuthDebug(event: string, data?: Record<string, unknown>): void {
    if (!isAuthDebugEnabled()) return;
    const entry: IAuthDebugEntry = {
        ts: new Date().toISOString(),
        event,
        data,
    };
    appendAuthEntry('[auth][debug]', entry);
}

export function logAuthUser(event: string, data?: Record<string, unknown>): void {
    if (!isAuthUserLogEnabled()) return;
    const entry: IAuthDebugEntry = {
        ts: new Date().toISOString(),
        event,
        data,
    };
    appendAuthEntry('[auth][user]', entry);
}

export function getAuthDebugLog(): IAuthDebugEntry[] {
    return readEntries();
}

export function clearAuthDebugLog(): void {
    if (!canUseWindow()) return;
    try {
        localStorage.removeItem(AUTH_DEBUG_STORAGE_KEY);
    } catch {
        // ignore
    }
}
